import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import StatCard from '../components/admin/StatCard';
import {
  IconWrench, IconCheckCircle, IconClock, IconBan, IconUsers,
  IconPlus, IconRefresh, IconTrash, IconPause, IconPlay, IconMail,
  IconAlertTriangle,
} from '../components/admin/icons';
import { useI18n } from '../i18n';

interface Installer {
  id: number;
  name: string;
  slug: string;
  status: string;
  visible: boolean;
  city: string | null;
  speciality: string | null;
  createdAt: string;
  clientCount: number;
}

type ConfirmAction = {
  kind: 'suspend' | 'activate' | 'delete';
  installer: Installer;
};

export default function AdminInstallersPage() {
  const { token } = useAuth();
  const { t, tp } = useI18n();
  const [installers, setInstallers] = useState<Installer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [actionRunning, setActionRunning] = useState(false);
  const [actionError, setActionError] = useState('');

  const API = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

  useEffect(() => { fetchInstallers(); }, []);

  async function fetchInstallers() {
    try {
      const res = await fetch(`${API}/api/admin/installers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setInstallers(data.installers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function askSuspend(inst: Installer) { setActionError(''); setConfirmAction({ kind: 'suspend', installer: inst }); }
  function askActivate(inst: Installer) { setActionError(''); setConfirmAction({ kind: 'activate', installer: inst }); }
  function askDelete(inst: Installer) { setActionError(''); setConfirmAction({ kind: 'delete', installer: inst }); }

  async function runConfirmedAction() {
    if (!confirmAction) return;
    setActionRunning(true);
    setActionError('');
    try {
      const { kind, installer } = confirmAction;
      let res: Response;
      if (kind === 'delete') {
        res = await fetch(`${API}/api/admin/installers/${installer.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        res = await fetch(`${API}/api/admin/installers/${installer.id}/${kind}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        const msg = typeof data?.error === 'string'
          ? data.error
          : data?.error?.message || t('adm_err_status', { status: res.status });
        setActionError(msg);
        setActionRunning(false);
        return;
      }
      setConfirmAction(null);
      fetchInstallers();
    } catch (e: any) {
      setActionError(e?.message || t('adm_err_network'));
    } finally {
      setActionRunning(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
      </div>
    );
  }

  const activeCount = installers.filter(i => i.status === 'active').length;
  const pendingCount = installers.filter(i => i.status === 'pending').length;
  const suspendedCount = installers.filter(i => i.status === 'suspended').length;
  const totalClients = installers.reduce((sum, i) => sum + (i.clientCount || 0), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl text-white shadow-2xl">
        {/* Image de fond — technicien / installation pro */}
        <img
          src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1600&h=600&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/90 to-teal-950/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
        {/* Pattern grille subtile */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        {/* Halos colorés */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-teal-500/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl" />

        <div className="relative p-7">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-5">
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 blur-lg opacity-60" />
                <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg ring-1 ring-white/20 text-white">
                  <IconWrench className="w-7 h-7" />
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.08] backdrop-blur ring-1 ring-cyan-400/30 text-cyan-300 text-[11px] font-semibold mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  {t('adm_console_badge')}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white drop-shadow-lg">
                  {t('adm_inst_title_1')}{' '}
                  <span className="bg-gradient-to-r from-teal-300 via-cyan-300 to-sky-300 bg-clip-text text-transparent">
                    {t('adm_inst_title_2')}
                  </span>
                </h1>
                <p className="text-slate-200 text-sm mt-1.5 drop-shadow">
                  {tp('adm_inst_certified', installers.length)}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowInvite(true)}
              className="group relative px-5 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white font-semibold shadow-lg shadow-cyan-500/30 transition-all hover:shadow-cyan-500/50 hover:-translate-y-0.5 inline-flex items-center gap-2"
            >
              <IconPlus className="w-4 h-4" /> {t('adm_inst_invite')}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <StatCard icon={IconCheckCircle} label={t('adm_inst_stat_active')} value={activeCount} color="emerald" total={installers.length || 1} compact />
            <StatCard icon={IconClock} label={t('adm_inst_stat_pending')} value={pendingCount} color="amber" total={installers.length || 1} compact />
            <StatCard icon={IconBan} label={t('adm_inst_stat_suspended')} value={suspendedCount} color="rose" total={installers.length || 1} compact />
            <StatCard icon={IconUsers} label={t('adm_inst_stat_clients')} value={totalClients} color="cyan" hint={t('adm_inst_stat_clients_hint')} compact />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-slate-800">{t('adm_inst_list')}</h2>
          <button onClick={fetchInstallers} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5">
            <IconRefresh className="w-3.5 h-3.5" /> {t('adm_refresh')}
          </button>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="text-left px-5 py-3 font-semibold text-slate-600 uppercase text-[11px] tracking-wider">{t('adm_inst_th_installer')}</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-[11px] tracking-wider">{t('adm_inst_th_city')}</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600 uppercase text-[11px] tracking-wider">{t('adm_inst_th_speciality')}</th>
              <th className="text-center px-4 py-3 font-semibold text-slate-600 uppercase text-[11px] tracking-wider">{t('adm_nav_clients')}</th>
              <th className="text-center px-4 py-3 font-semibold text-slate-600 uppercase text-[11px] tracking-wider">{t('adm_th_status')}</th>
              <th className="text-right px-5 py-3 font-semibold text-slate-600 uppercase text-[11px] tracking-wider">{t('adm_th_actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {installers.map(inst => {
              const initials = inst.name.slice(0, 2).toUpperCase();
              return (
              <tr key={inst.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm">
                      {initials}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{inst.name}</p>
                      <p className="text-[11px] text-slate-400 font-mono">{inst.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{inst.city || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-slate-600">{inst.speciality || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs font-bold">
                    {inst.clientCount}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full ${
                    inst.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    inst.status === 'suspended' ? 'bg-rose-100 text-rose-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      inst.status === 'active' ? 'bg-emerald-500' :
                      inst.status === 'suspended' ? 'bg-rose-500' :
                      'bg-amber-500'
                    }`} />
                    {inst.status === 'active' ? t('adm_inst_st_active') : inst.status === 'suspended' ? t('adm_inst_st_suspended') : t('adm_inst_st_pending')}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {inst.status === 'active' ? (
                      <button
                        onClick={() => askSuspend(inst)}
                        className="px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-md transition-colors inline-flex items-center gap-1"
                      >
                        <IconPause className="w-3.5 h-3.5" /> {t('adm_inst_suspend')}
                      </button>
                    ) : inst.status === 'suspended' ? (
                      <button
                        onClick={() => askActivate(inst)}
                        className="px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors inline-flex items-center gap-1"
                      >
                        <IconPlay className="w-3.5 h-3.5" /> {t('adm_inst_reactivate')}
                      </button>
                    ) : null}
                    <button
                      onClick={() => askDelete(inst)}
                      className="px-2.5 py-1 text-xs font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-md transition-colors inline-flex items-center gap-1"
                      title={t('adm_inst_delete_forever')}
                    >
                      <IconTrash className="w-3.5 h-3.5" /> {t('adm_delete')}
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
            {installers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-slate-400 text-sm">
                  {t('adm_inst_empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {showInvite && (
        <InviteInstallerModal
          token={token!}
          apiUrl={API}
          onClose={() => setShowInvite(false)}
          onDone={() => { setShowInvite(false); fetchInstallers(); }}
        />
      )}

      {confirmAction && (
        <ConfirmActionModal
          action={confirmAction}
          running={actionRunning}
          error={actionError}
          onCancel={() => !actionRunning && setConfirmAction(null)}
          onConfirm={runConfirmedAction}
        />
      )}
    </div>
  );
}

// ─── Modal de confirmation premium ───
function ConfirmActionModal({
  action, running, error, onCancel, onConfirm,
}: {
  action: ConfirmAction;
  running: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { kind, installer } = action;
  const { t } = useI18n();
  const config = {
    suspend: {
      title: t('adm_inst_c_suspend_title'),
      subtitle: t('adm_inst_c_suspend_sub'),
      headerFrom: 'from-amber-600',
      headerTo: 'to-orange-700',
      btnFrom: 'from-amber-600',
      btnTo: 'to-orange-600',
      btnHoverFrom: 'hover:from-amber-500',
      btnHoverTo: 'hover:to-orange-500',
      icon: <IconPause className="w-6 h-6" />,
      cta: t('adm_inst_c_suspend_cta'),
      ctaIcon: <IconPause className="w-4 h-4" />,
      consequences: [
        t('adm_inst_c_suspend_1'),
        t('adm_inst_c_suspend_2'),
        t('adm_inst_c_suspend_3'),
      ],
    },
    activate: {
      title: t('adm_inst_c_activate_title'),
      subtitle: t('adm_inst_c_activate_sub'),
      headerFrom: 'from-emerald-600',
      headerTo: 'to-teal-700',
      btnFrom: 'from-emerald-600',
      btnTo: 'to-teal-600',
      btnHoverFrom: 'hover:from-emerald-500',
      btnHoverTo: 'hover:to-teal-500',
      icon: <IconPlay className="w-6 h-6" />,
      cta: t('adm_inst_c_activate_cta'),
      ctaIcon: <IconPlay className="w-4 h-4" />,
      consequences: [
        t('adm_inst_c_activate_1'),
        t('adm_inst_c_activate_2'),
        t('adm_inst_c_activate_3'),
      ],
    },
    delete: {
      title: t('adm_inst_c_delete_title'),
      subtitle: t('adm_inst_c_delete_sub'),
      headerFrom: 'from-rose-600',
      headerTo: 'to-red-700',
      btnFrom: 'from-rose-600',
      btnTo: 'to-red-600',
      btnHoverFrom: 'hover:from-rose-500',
      btnHoverTo: 'hover:to-red-500',
      icon: <IconAlertTriangle className="w-6 h-6" />,
      cta: t('adm_inst_delete_forever'),
      ctaIcon: <IconTrash className="w-4 h-4" />,
      consequences: [
        t('adm_inst_c_delete_1'),
        t('adm_inst_c_delete_2'),
        t('adm_inst_c_delete_3'),
        t('adm_inst_c_delete_4'),
      ],
    },
  }[kind];

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className={`bg-gradient-to-br ${config.headerFrom} ${config.headerTo} p-6 text-white`}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur ring-1 ring-white/20 flex items-center justify-center shrink-0">
              {config.icon}
            </div>
            <div>
              <h2 className="text-xl font-bold">{config.title}</h2>
              <p className="text-sm text-white/85 mt-1">{config.subtitle}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold shrink-0">
              {installer.name.slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 truncate">{installer.name}</p>
              <p className="text-xs text-slate-500 truncate">
                {[installer.city, installer.speciality].filter(Boolean).join(' • ') || installer.slug}
              </p>
            </div>
          </div>

          <div className="text-sm text-slate-600">
            <p className="font-semibold text-slate-800 mb-2">{t('adm_inst_consequences')}</p>
            <ul className="list-disc pl-5 space-y-1 text-slate-600">
              {config.consequences.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onCancel}
              disabled={running}
              className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 font-medium text-sm disabled:opacity-50"
            >
              {t('adm_cancel')}
            </button>
            <button
              onClick={onConfirm}
              disabled={running}
              className={`px-5 py-2 bg-gradient-to-r ${config.btnFrom} ${config.btnTo} ${config.btnHoverFrom} ${config.btnHoverTo} text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm shadow inline-flex items-center gap-2`}
            >
              {running ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  {t('adm_in_progress')}
                </>
              ) : (
                <>{config.ctaIcon} {config.cta}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InviteInstallerModal({
  token, apiUrl, onClose, onDone,
}: {
  token: string; apiUrl: string; onClose: () => void; onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [city, setCity] = useState('');
  const [speciality, setSpeciality] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { t } = useI18n();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const res = await fetch(`${apiUrl}/api/admin/installers/invite`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, companyName, city: city || undefined, speciality: speciality || undefined }),
    });
    const data = await res.json();

    if (data.success) {
      onDone();
    } else {
      const errMsg = typeof data.error === 'string' ? data.error : data.error?.message || t('adm_err_generic');
      setError(errMsg);
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="bg-gradient-to-br from-slate-900 to-teal-900 p-6 text-white">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shrink-0 shadow-lg text-white">
              <IconWrench className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{t('adm_inst_invite')}</h2>
              <p className="text-sm text-slate-300 mt-1">{t('adm_inst_invite_sub')}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">{t('adm_inst_f_email')}</label>
            <input type="email" required placeholder="contact@installateur.fr" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">{t('adm_inst_f_company')}</label>
            <input type="text" required placeholder={t('adm_inst_ph_company')} value={companyName} onChange={e => setCompanyName(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">{t('adm_inst_th_city')}</label>
              <input type="text" placeholder="Toulouse" value={city} onChange={e => setCity(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">{t('adm_inst_th_speciality')}</label>
              <input type="text" placeholder={t('adm_inst_ph_speciality')} value={speciality} onChange={e => setSpeciality(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">{error}</div>
          )}
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-100 transition-all">
            {t('adm_cancel')}
          </button>
          <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all inline-flex items-center justify-center gap-2 shadow">
            {submitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                {t('adm_sending')}
              </>
            ) : (
              <>
                <IconMail className="w-4 h-4" /> {t('adm_inst_send_invite')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
